import * as CONSTANTS from "../utils/constants/constants";

class PatientService {
  static getPatients(
    organization_id: string,
    history_days: number = 0,
    disabled = false
  ) {
    var url = CONSTANTS.HOST + "api/v1/rpm/patients/" + organization_id + "/";
    //console.log("Get patients:", url);
    url = url + "?disabled=" + disabled;
    if (history_days > 0) {
      url = url + "&engagementhistory=" + history_days;
    }
    return url;
  }

  // Patient avatar APIs
  static async getPatientImage(access_token: string, user_id: string) {
    const url = CONSTANTS.HOST + `api/v1/rpm/patients/${user_id}/image/`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
      });
      if (!response.ok) {
        // Return null on 403/404 to allow graceful UI fallback
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error("Failed to fetch patient image:", error);
      return null;
    }
  }

  static async setPatientImage(access_token: string, patient_id: string, dataUri: string) {
    const url = CONSTANTS.HOST + `api/v1/rpm/patients/${patient_id}/image/`;
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({ image: dataUri }),
      });
      if (!response.ok) {
        throw new Error(`Error setting patient image: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Failed to set patient image:", error);
      throw error;
    }
  }

  static async deletePatientImage(access_token: string, patient_id: string) {
    const url = CONSTANTS.HOST + `api/v1/rpm/patients/${patient_id}/image/`;
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
      });
      if (!response.ok) {
        throw new Error(`Error deleting patient image: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Failed to delete patient image:", error);
      throw error;
    }
  }

  static async registerPatient(access_token: string, patientdata: any) {
    const url = CONSTANTS.HOST + "api/v2/registration/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify(patientdata),
      });

      return await response.json();
    } catch (error) {
      //console.error("Failed to add patient:", error);
      return null;
    }
  }

  static async setPatientState(
    access_token: string,
    patient_id: string,
    newState: boolean
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/patientstate/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({ patient_id: patient_id, disabled: newState }),
      });

      return await response.json();
    } catch (error) {
      console.error("Failed to update patient state:", error);
      return null;
    }
  } // setPatientState

  static async setPatientBaselines(
    access_token: string,
    patient_id: string,
    measurements: any
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/baseline/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({
          patient_id: patient_id,
          measurements: measurements,
        }),
      });

      return await response.json();
    } catch (error) {
      console.error("Failed to update baseline measurements:", error);
      return null;
    }
  } // setPatientBaselines

  // Retrieve existing baseline vitals for a patient
  static async getPatientBaselines(access_token: string, patient_id: string) {
    const url = CONSTANTS.HOST + `api/v1/rpm/baseline/?patient_id=${encodeURIComponent(patient_id)}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
      });
      if (!response.ok) {
        // 400/403/404 are possible; surface null for graceful UI fallback
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error("Failed to fetch patient baselines:", error);
      return null;
    }
  }

  /**
   * Fetch detailed engagement data for a specific patient and date.
   */
  static async fetchEngagementDetails(
    access_token: string,
    patient_id: string,
    date: string
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/todocheck/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({ patient_id, date }),
      });

      if (!response.ok) {
        throw new Error(
          `Error fetching engagement details: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to fetch engagement details:", error);
      return null;
    }
  }

  /**
   * Log a call session as a measurement record.
   */
  static async logCall(
    access_token: string,
    patient_id: string,
    organization_id: string,
    physician: boolean,
    start_time: Date,
    duration: number
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/insert_measurement_record/";

    const val = {
      physician: physician,
      start_time: start_time.toISOString(),
      duration: duration,
    };

    const requestBody = {
      patient_id: patient_id,
      org: organization_id,
      measurement_type: "CallSession",
      source: "Cellphone",
      recorded_at: start_time.toISOString(),
      value: val,
      status_code: 0,
      data_id: crypto.randomUUID(),
      loinc_code: "34748-4",
      display_name: "Telephone encounter Note",
      unit: "min",
      unit_code: "min",
      system: "http://loinc.org",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Error logging call: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to log call:", error);
      return null;
    }
  }

  /**
   * Fetch engagement plan for a patient (optional patient_id).
   */
  static async fetchEngagementPlan(access_token: string, patient_id?: string) {
    let url = CONSTANTS.HOST + "api/v1/rpm/engagementplans/";
    if (patient_id) {
      url += patient_id + "/";
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
      });

      if (!response.ok) {
        console.error(`Error fetching engagement plans: ${response.statusText}`);
        //throw new Error(`Error fetching engagement plans: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log("Raw API response from", url, ":", responseData);
      return responseData;
    } catch (error) {
      console.error("Failed to fetch engagement plans:", error);
      return null;
    }
  }

  /**
   * Fetch available engagement plans (without patient_id).
   */
  static async fetchEngagementPlans(access_token: string) {
    const url = CONSTANTS.HOST + "api/v1/rpm/engagementplans/";

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Error fetching engagement plans: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to fetch engagement plans:", error);
      return null;
    }
  }

  /**
   * Assign an engagement plan to a patient.
   */
  static async assignEngagementPlan(
    access_token: string,
    patient_id: string,
    plans: string[],
    start_date: string,
    selected_measurements?: string[]
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/assignplan/";

    try {
      //console.log ("Assign plan:", patient_id, plans, start_date);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({
          patient_id: patient_id,
          patient_groups: plans,
          start_date: start_date,
          // Optional: allow per-measurement selection if backend supports it.
          ...(selected_measurements && selected_measurements.length
            ? { selected_measurements }
            : {}),
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Error assigning engagement plan: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to assign engagement plan:", error);
      return null;
    }
  }

  /**
   * Assign an engagement plan with custom plan details to a patient.
   */
  static async assignEngagementPlanWithCustomDetails(
    access_token: string,
    patient_id: string,
    plans: string[],
    start_date: string,
    selected_measurements: string[],
    custom_plan_details: any
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/assignplan/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({
          patient_id: patient_id,
          patient_groups: plans,
          start_date: start_date,
          selected_measurements: selected_measurements,
          custom_plan_details: custom_plan_details,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Error assigning engagement plan with custom details: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to assign engagement plan with custom details:", error);
      return null;
    }
  }

  static async getMeasurementHistory(
    access_token: string,
    patient_id: string,
    organization_id: string,
    start_time: string,
    end_time: string
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/measurementhistory/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({
          patient_id: patient_id,
          org: organization_id,
          start_time: start_time,
          end_time: end_time,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error retrieving history: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to retrieve history:", error);
      return null;
    }
  }

  static async fetchTimeData(access_token: string, patientId: string) {
    const url = CONSTANTS.HOST + "api/v1/rpm/time-tracking/" + patientId + "/";
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching time data: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to fetch engagement plans:", error);
      return null;
    }
  }

  static async setTimeData(
    access_token: string,
    patientId: string,
    newLog: any
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/time-tracking/" + patientId + "/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify(newLog),
      });

      if (!response.ok) {
        throw new Error(`Error setting timelog: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to set timelog:", error);
      return null;
    }
  }

  static async registerTraining(
    access_token: string,
    user_id: string,
    organization_id: string,
    patientId: string,
    timestamp: string
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/journalentry/";

    const data = {
      patient_id: patientId,
      organization_id: organization_id,
      source: "Clinician",
      recorded_at: timestamp,
      entry_type: "training",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Error registering training: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to register training:", error);
      return null;
    }
  }

  static async getMeasurements(
    access_token: string,
    patient_id: string,
    organization_id: string,
    measurement_type: string,
    start_time: string,
    end_time: string
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/get_measurement_record/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({
          patient_id: patient_id,
          org: organization_id,
          measurement_type: measurement_type,
          start_time: start_time,
          end_time: end_time,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Error retrieving measurements: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to retrieve measurements:", error);
      return null;
    }
  }

  static async fetchCallLog(
    access_token: string,
    patientId: string,
    organizationId: string,
    month: string
  ) {
    const url = CONSTANTS.HOST + "api/v1/rpm/call-log/";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify({
          patient_id: patientId,
          org: organizationId,
          month: month,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error retrieving call log: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to retrieve call log:", error);
      return null;
    }
  }

  static async updatePatient(access_token: string, patientData: any) {
    const url = CONSTANTS.HOST + "api/v1/rpm/editpatient/";

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify(patientData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update patient");
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to update patient:", error);
      throw error;
    }
  }

  /**
   * Resend invitation email to a patient.
   */
  static async resendInvitation(
    access_token: string,
    patient_id: string,
    cc_emails?: string[]
  ) {
    const url = CONSTANTS.HOST + "api/resend-invitation/";
    
    const requestBody = {
      "elation-id": patient_id,
      ...(cc_emails && cc_emails.length > 0 ? { cc_emails } : {}),
    };

    console.log("Resend invitation request:", {
      url,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + (access_token ? access_token.substring(0, 10) + "..." : "NO_TOKEN"),
      },
      body: requestBody,
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + access_token,
        },
        body: JSON.stringify(requestBody),
      });

      console.log("Resend invitation response:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Resend invitation error response:", errorText);
        throw new Error(
          `Error resending invitation: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log("Resend invitation success:", result);
      return result;
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      throw error;
    }
  }
}

export default PatientService;
